import React, { useEffect, useState } from "react";
import { setTitle } from "../../helpers/MetaTag";
import useFetch from "../../hooks/useFetch";
import { getBlogList } from "../../services/BlogService";
import dayjs from "dayjs";
import AnnouncementItem from "../../components/sections/front/Common/AnnouncementItem";
import { scholarshipList } from "../../services/scholarshipService";
import Table from "../../components/elements/table/Table";
import { checkDateStatus, handleFilterChange } from "../../helpers";
import { useDebounce } from "../../hooks/useDebounce";
import Search from "../../components/form/Search";
import Button from "../../components/form/Button";
import Modal from "../../components/elements/Modal";
import ApplicationDetails from "../../components/modalcontent/ApplicationDetails";
import EligibilityQuizContent from "../../components/modalcontent/EligibilityQuizContent";
import ApplicationModalFront from "../../components/sections/front/Apply/modals/ApplicationModalFront";
import ApplicationNotOpenInfoModal from "../../components/sections/front/Apply/modals/ApplicationNotOpenInfoModal";

const Dashboard = () => {
  setTitle("Vpeters User | Dashboard");
  const [keyword, setKeyword] = useState("");
  const [filters, setFilters] = useState({
    limit: 5,
    page: 1,
    sortQuery: "ordering",
  });
  const [applicationModal, setApplicationModal] = useState({
    isOpen: false,
    data: null,
  });
  const search = useDebounce(keyword, 300);
  const [event, setActiveEvent] = useState();
  const [action, setAction] = useState(false);
  const [isApply, setApply] = useState({ isOpen: false, data: null });
  const [isViewDetails, setViewDetails] = useState({
    isOpen: false,
    data: null,
  });
  const [notOpenQuizModal, setNotOpenQuizModal] = useState({
    isOpen: false,
    data: null,
  });

  const {
    data: events,
    error,
    isLoading,
    updateData,
    isFetching,
    refetch,
  } = useFetch({
    dependencies: ["Dashboard"],
    fetchFunction: async () => {
      const res = await getBlogList({
        type: "event",
        limit: 3,
        sortQuery: "-createdAt",
        upcomingEvent: true,
        status: 1,
      });
      if (res?.docs?.status === 200) {
        return (res?.docs?.data || [])?.map((item) => ({
          ...item,
          _id: item?._id,
          date: {
            day: dayjs(item?.publishDate)?.format("DD"),
            month: dayjs(item?.publishDate)?.format("MMMM"),
          },
          image: item?.image?.url,
          link: `/updates/${item?.slug || item?._id}`,
        }));
      } else {
        return [];
      }
    },
  });

  const {
    data: timelineData,
    isLoading: timelineLoading,
    refetch: scholarShip,
  } = useFetch({
    dependencies: ["scholarshipList", filters, search],
    fetchFunction: async () => {
      const res = await scholarshipList({
        ...filters,
        ...(search && { keyword: search }),
        status: 1,
      });

      return res;
    },
  });

  const tableHeadData = [
    {
      _id: 2,
      name: "Scholarship Title",
      align: "left",
    },
    {
      _id: 3,
      name: "Application Year",
      align: "left",
    },
    {
      _id: 4,
      name: "Deadline",
      align: "left",
    },
    {
      _id: 31,
      name: " Actions",
      align: "center",
    },
  ];

  useEffect(() => {
    if (action) {
      scholarShip();
    }
  }, [action]);

  const getApplyButtonProps = (item) => {
    const {
      isQualified,
      publishEndDate,
      isStartedApplying,
      publishDate,
      statusDetails,
    } = item;

    const isPast = checkDateStatus(publishEndDate) === "past";
    const isUpcoming = checkDateStatus(publishDate) === "upcoming";

    if (isUpcoming) {
      return {
        label: "Opening Soon",
        onClick: () => setNotOpenQuizModal({ data: item, isOpen: true }),
      };
    }

    if (isQualified) {
      if (isPast && isStartedApplying === "notStarted") {
        return { label: "Currently Unavailable" };
      }
      if (statusDetails?.title === "Draft") {
        return {
          label: "Incomplete/Draft",
          onClick: () => setApply({ isOpen: true, data: item }),
        };
      }
      if (statusDetails?.title === "Not Selected") {
        return {
          label: "Not Selected",
        };
      }
      switch (isStartedApplying) {
        case "completed":
          return { label: "Applied" };
        case "ongoing":
          return {
            label: "Incomplete/Draft",
            onClick: () => setApply({ isOpen: true, data: item }),
          };
        case "notStarted":
          return {
            label: "Apply",
            onClick: () => setApply({ isOpen: true, data: item }),
          };
        default:
          return { label: "Currently Unavailable" };
      }
    } else {
      if (isStartedApplying === "completed") {
        return { label: "Applied" };
      }

      return {
        label: isStartedApplying === "ongoing" ? "Incomplete/Draft" : "Apply",
        onClick: () => setApply({ isOpen: true, data: item }),
      };
    }
  };

  const tableData = [...(timelineData?.docs?.data || [])]?.map((item) => {
    return {
      _id: item?._id,
      rowData: [
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.title || "N/A",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.publishDate
            ? dayjs(item.publishDate).format("YYYY")
            : "N/A",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: item?.publishDate
            ? dayjs(item.publishDate).format("YYYY")
            : "N/A",
        },
        {
          _id: 3,
          type: "text",
          align: "center",
          cellClass: "!min-w-[100px] gap-2",
          data: (
            <div className="flex items-center justify-center gap-4">
              {/* View Details */}
              <div
                className="w-28 text-center text-[#C94E53] cursor-pointer"
                onClick={() => setViewDetails({ isOpen: true, data: item })}
              >
                View Details
              </div>

              {/* Divider */}
              <div className="w-1 h-4 bg-gray-200" />

              {/* Apply Status */}
              <div className="w-28 text-center text-[#C94E53]">
                {(() => {
                  const { label, onClick } = getApplyButtonProps(item);
                  return onClick ? (
                    <span className="cursor-pointer" onClick={onClick}>
                      {label}
                    </span>
                  ) : (
                    <span className="whitespace-nowrap">{label}</span>
                  );
                })()}
              </div>
            </div>
          ),
        },
      ],
    };
  });

  function onApplyQuizHandler(item) {
    const today = new Date();
    const startData = new Date(item?.publishDate);
    if (today >= startData) {
      setApply({ isOpen: true, data: item });
    } else {
      setNotOpenQuizModal({ data: item, isOpen: true });
    }
  }

  return (
    <>
      <h1 className="text-3xl font-bold text-[#C94E53]  px-6 py-4 rounded-2xl text-center ">
        Dashboard
      </h1>
      <h1 className="text-base font-bold   px-3 py-2 rounded-xl  bg-white mb-2">
        Thank you for being part of the V’Peters Family Scholarship! Explore
        opportunities, apply, and stay connected to our latest events through
        our calendar.
      </h1>
      <div className="w-full md:w-72 my-3 ml-auto">
        <Search
          placeholder={"Search here"}
          search={(val) => {
            setKeyword(val);
            handleFilterChange("page", 1, setFilters);
          }}
        />
      </div>

      <div className="mb-2">
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={timelineLoading}
          pageCount={timelineData?.docs?.metadata?.totalPages}
          // currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
          }}
          // handleOrdering={(newData, oldData) => {
          //   messageOrderingHandler(newData, oldData);
          // }}
          originalData={[...(timelineData?.docs?.data || [])]}
          // setOriginalData={(data) =>
          //   updateData((pre) => {
          //     pre.data = data;
          //     return { ...pre };
          //   })
          // }
          // isFetching={isFetching}
          isOrderingDisabled={true}
          // selectedItems={selectedItems}
          // setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
        />
      </div>
      <div>
        {(events || []).map((item, index) => (
          <AnnouncementItem key={index} index={index} data={item} />
        ))}
      </div>
      <Modal
        isOpen={isViewDetails?.isOpen}
        setIsOpen={() => setViewDetails({ isOpen: false, data: null })}
        title={isViewDetails?.data?.title || ""}
        size="xl5"
        titleClass={"!text-black"}
        headerClass={"border-b border-slate-200"}
        bodyClasses={"px-8 md:px-14"}
      >
        <ApplicationDetails
          data={isViewDetails?.data}
          onApply={(data) => {
            setApply({ isOpen: true, data });
            setViewDetails({ isOpen: false, data: null });
          }}
        />
      </Modal>
      <Modal
        isOpen={isApply?.isOpen}
        setIsOpen={() => {
          setApply({ isOpen: false, data: null });
        }}
        title={"Eligibility Quiz"}
        size="xl2"
      >
        <EligibilityQuizContent
          data={isApply?.data}
          okHandler={() => setApply({ isOpen: false, data: null })}
          setApplicationModal={setApplicationModal}
          setAction={setAction}
        />
      </Modal>
      <ApplicationNotOpenInfoModal
        isOpen={notOpenQuizModal?.isOpen}
        close={() => {
          setNotOpenQuizModal({ isOpen: false, data: null });
        }}
        data={notOpenQuizModal?.data}
      />
      {applicationModal.isOpen && (
        <ApplicationModalFront
          open={applicationModal?.isOpen}
          close={() => {
            setApplicationModal({ isOpen: false, data: null });
            setAction(true);
          }}
          details={applicationModal.data}
        />
      )}
    </>
  );
};

export default Dashboard;
